import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, User, Guest } from '../types/user';
import { useAuth } from './SupabaseAuthContext';

interface ProjectContextType {
  currentProject: Project | null;
  projects: Project[];
  createProject: (name: string) => Project;
  saveProject: (projectData: Partial<Project>) => void;
  loadProject: (projectId: string | Project) => void;
  deleteProject: (projectId: string) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

interface ProjectProviderProps {
  children: ReactNode;
}

function getProjectsKey(user: User | Guest | null): string {
  if (!user) return 'projects_guest';
  if (user.type === 'guest') return `projects_guest_${user.id}`;
  return `projects_${user.id}`;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  useEffect(() => {
    const projectsKey = getProjectsKey(user);
    const savedProjects = localStorage.getItem(projectsKey);
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    } else {
      setProjects([]);
    }
  }, [user]);

  const createProject = (name: string): Project => {
    if (user?.type === 'guest' && user.allowedFeatures?.maxProjects === 1 && projects.length >= 1) {
      alert('Guests can only create one project. Sign up for a free account to create more!');
      throw new Error('Guest project limit reached');
    }
    const newProject: Project = {
      id: `proj_${Date.now()}`,
      name,
      ownerId: user?.id || 'guest',
      content: {}, // Default empty content
      isPublic: false,
      tags: [],
      lastModified: new Date().toISOString(),
    };
    const updatedProjects = [...projects, newProject];
    setProjects(updatedProjects);
    localStorage.setItem(getProjectsKey(user), JSON.stringify(updatedProjects));
    setCurrentProject(newProject);
    return newProject;
  };

  const saveProject = (projectData: Partial<Project>) => {
    if (user?.type === 'guest' && user.allowedFeatures?.canSaveLocally === false) {
      alert('Saving is disabled for guests. Sign up for a free account to save your work!');
      return;
    }
    if (!currentProject) return;
    const updatedProject = { ...currentProject, ...projectData, lastModified: new Date().toISOString() };
    const updatedProjects = projects.map(p => (p.id === updatedProject.id ? updatedProject : p));
    setProjects(updatedProjects);
    setCurrentProject(updatedProject as Project);
    localStorage.setItem(getProjectsKey(user), JSON.stringify(updatedProjects));
  };

  const loadProject = (projectId: string | Project) => {
    if (typeof projectId === 'string') {
      // Loading existing project by ID
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setCurrentProject(project);
      }
    } else {
      // Loading demo project - convert to Project format
      const workspaceData = typeof projectId.workspace === 'string' ? 
        JSON.parse(projectId.workspace) : 
        (projectId.workspace || projectId.content || {});
        
      const demoProject: Project = {
        id: projectId.id,
        name: projectId.title || projectId.name,
        ownerId: user?.id || 'guest',
        content: workspaceData,
        workspaceState: workspaceData, // Add this for the editor
        isPublic: false,
        tags: projectId.tags || [],
        lastModified: new Date().toISOString(),
        description: projectId.description,
        category: projectId.category
      };
      setCurrentProject(demoProject);
    }
  };

  const deleteProject = (projectId: string) => {
    const updatedProjects = projects.filter(p => p.id !== projectId);
    setProjects(updatedProjects);
    localStorage.setItem(getProjectsKey(user), JSON.stringify(updatedProjects));
    if (currentProject?.id === projectId) {
      setCurrentProject(null);
    }
  };

  const value: ProjectContextType = {
    currentProject,
    projects,
    createProject,
    saveProject,
    loadProject,
    deleteProject,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};